// Haptic feedback patterns for native mobile feel
// Uses the Vibration API where available

type HapticPattern = 'light' | 'medium' | 'heavy' | 'success' | 'warning' | 'error' | 'selection';

interface HapticOptions {
  pattern: HapticPattern;
  fallbackToVisual?: boolean;
}

// Vibration patterns in milliseconds
const HAPTIC_PATTERNS: Record<HapticPattern, number | number[]> = {
  light: 10,
  medium: 25,
  heavy: 50,
  success: [10, 50, 30],
  warning: [30, 50, 30, 50, 30],
  error: [50, 100, 50],
  selection: 5,
};

export const useHapticFeedback = () => {
  const isSupported = typeof navigator !== 'undefined' && 'vibrate' in navigator;

  const triggerHaptic = (options: HapticOptions | HapticPattern) => {
    const pattern = typeof options === 'string' ? options : options.pattern;
    
    if (!isSupported) {
      // Fallback: could trigger visual feedback here
      return false;
    }

    try {
      const vibrationPattern = HAPTIC_PATTERNS[pattern];
      navigator.vibrate(vibrationPattern);
      return true;
    } catch (error) {
      console.warn('Haptic feedback failed:', error);
      return false;
    }
  };

  // Convenience methods
  const light = () => triggerHaptic('light');
  const medium = () => triggerHaptic('medium');
  const heavy = () => triggerHaptic('heavy');
  const success = () => triggerHaptic('success');
  const warning = () => triggerHaptic('warning');
  const error = () => triggerHaptic('error');
  const selection = () => triggerHaptic('selection');

  return {
    isSupported,
    triggerHaptic,
    light,
    medium,
    heavy,
    success,
    warning,
    error,
    selection,
  };
};

// Standalone function for use outside of React components
export const hapticFeedback = (pattern: HapticPattern = 'light'): boolean => {
  if (typeof navigator === 'undefined' || !('vibrate' in navigator)) {
    return false;
  }

  try {
    const vibrationPattern = HAPTIC_PATTERNS[pattern];
    navigator.vibrate(vibrationPattern);
    return true;
  } catch {
    return false;
  }
};
